import { useTranslation } from 'react-i18next';
import type { MethodContent } from '@/types/methodologies';

interface CardsViewProps {
  methodId: string;
  content: MethodContent;
}

/**
 * Default layout: one card per section, in a responsive 1/2/3-column grid.
 * Used by `product-vision` and `jtbd` in wave A, and by any future
 * `cards`-layout methodology — section titles come from the shared
 * `methodologies:sections.<methodId>.<key>` map, not from the AI's own text,
 * so the same key always reads the same way regardless of model phrasing.
 */
export function CardsView({ methodId, content }: CardsViewProps) {
  const { t } = useTranslation('methodologies');

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[16px]">
        {content.sections.map((section) => (
          <div key={section.key} className="bg-[#f4f4f3] rounded-[16px] p-[24px] flex flex-col gap-[12px]">
            <h3 className="font-manrope font-semibold text-[16px] leading-[24px] text-[#0d0d0d]">
              {t(`sections.${methodId}.${section.key}`, { defaultValue: section.title })}
            </h3>
            {section.note && (
              <p className="font-segoe text-[13px] leading-[18px] text-[#565858] italic">
                {section.note}
              </p>
            )}
            <ul className="flex flex-col gap-[8px]">
              {section.items.map((item, index) => (
                <li key={index} className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] pl-[16px] relative">
                  <span className="absolute left-0 top-[8px] w-[4px] h-[4px] rounded-full bg-[#2197f1]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
